import React, { useState, useEffect } from "react";
import { FaTimesCircle, FaCopy, FaFileCsv, FaFileExcel, FaFilePdf, FaInfoCircle, FaCogs } from "react-icons/fa";
import axios from "axios";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { BaseUrl } from "../App";
import { Circles } from 'react-loader-spinner';
import { FadeLoader } from 'react-spinners';



// Responsive Export Component
const Export = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [selectedMethods, setSelectedMethods] = useState([]);
  const [showAddonsModal, setShowAddonsModal] = useState(false);
  const [showMethodsModal, setShowMethodsModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch service data on mount
  useEffect(() => {
    const fetchServicesData = async () => {
      setLoading(true);
      try {
        const response = await axios.post(
          `${BaseUrl}/backend/api/appointment_details.php`,
          {
            action: "get_all_bookings",
            api_key: "1",
          }
        );

        if (response.data.status === "true") {
          setServices(response.data.response);
        } else {
          console.error("Failed to load services.");
        }
      } catch (error) {
        console.error("Error fetching services:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchServicesData();
  }, []);

  const handleAddonsClick = (service) => {
    setSelectedService(service);
    setSelectedAddons(service.addons_details || []);
    setShowAddonsModal(true);
  };

  const handleMethodsClick = (service) => {
    setSelectedService(service);
    setSelectedMethods(service.unit_details || []);
    setShowMethodsModal(true);
  };

  // Close Modals
  const closeAddonsModal = () => setShowAddonsModal(false);
  const closeMethodsModal = () => setShowMethodsModal(false);

  // Handle Copy functionality
  const handleCopy = () => {
    const tableContent = filteredServices
      .map((service) => {
        return [
          service.id,
          service.service_title,
          service.order_date,
          service.start_date,
          service.client_name,
          service.client_phone,
          service.client_address,
          service.booking_status,
        ];
      })
      .join("\n");
  
    if (navigator?.clipboard) {
      navigator.clipboard.writeText(tableContent)
        .then(() => {
          alert("Table data copied to clipboard!");
        })
        .catch((error) => {
          console.error("Error copying to clipboard: ", error);
          alert("Failed to copy data. Please try again.");
        });
    } else {
      const textArea = document.createElement('textarea');
      textArea.value = tableContent;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        alert("Table data copied to clipboard!");
      } catch (err) {
        console.error("Error using execCommand: ", err);
        alert("Failed to copy data. Please try again.");
      }
      document.body.removeChild(textArea);
    }
  };

  const handleChange = (event) => {
    setEntriesPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value.toLowerCase());
    setCurrentPage(1);
  };
  
  // Handle pagination
  const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const handleNextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const handlePageChange = (page) => setCurrentPage(page);

  // Filter services based on search query
  const filteredServices = services.filter((service) => {
    const serviceTitle = service.service_title ? service.service_title.toLowerCase() : '';
    const clientName = service.client_name ? service.client_name.toLowerCase() : '';
    const orderDate = service.order_date ? service.order_date.toLowerCase() : '';
    const startDate = service.start_date ? service.start_date.toLowerCase() : '';
    const clientPhone = service.client_phone ? service.client_phone.toLowerCase() : '';
    
    return (
      serviceTitle.includes(searchQuery) ||
      clientName.includes(searchQuery) ||
      orderDate.includes(searchQuery) ||
      startDate.includes(searchQuery) ||
      clientPhone.includes(searchQuery)
    );
  });

  // Pagination logic
  const startIndex = (currentPage - 1) * entriesPerPage;
  const paginatedServices = filteredServices.slice(
    startIndex,
    startIndex + entriesPerPage
  );
  const totalPages = Math.ceil(filteredServices.length / entriesPerPage);

  // Export to Excel
  const handleExcelExport = () => {
    const columns = [
      "Service ID", "Service Title", "Order Date", "App Date", "Customer", "Phone", "Address", "Status"
    ];
    const rows = services.map((service) => [
      service.id,
      service.service_title,
      service.order_date,
      service.start_date,
      service.client_name,
      service.client_phone,
      service.client_address,
      service.booking_status,
    ]);
  
    const ws = XLSX.utils.aoa_to_sheet([columns, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Services Data");
    XLSX.writeFile(wb, "services-data.xlsx");
  };

  // Export to PDF
  const handlePdfExport = () => {
    const doc = new jsPDF();
    const columns = [
      "Service ID", "Service Title", "Order Date", "App Date", "Customer", "Phone", "Address", "Status"
    ];
    const rows = services.map((service) => [
      service.id,
      service.service_title,
      service.order_date,
      service.start_date,
      service.client_name,
      service.client_phone,
      service.client_address,
      service.booking_status,
    ]);
  
    doc.autoTable({
      head: [columns],
      body: rows,
      startY: 20,
      theme: "grid",
      margin: { horizontal: 10 },
      headStyles: {
        fillColor: [0, 123, 255],
        textColor: 255,
      },
    });
    doc.save("services-data.pdf");
  };

  const handleCsvExport = () => {
    const csvData = services.map((service) => [
      service.id,
      service.service_title,
      service.order_date,
      service.start_date,
      service.client_name,
      service.client_phone,
      service.client_address,
      service.booking_status,
    ]);
  
    const csvContent = `data:text/csv;charset=utf-8,${csvData.join("\n")}`;
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "services-data.csv");
    link.click();
  };

  // Loading state
  // if (loading) return <p className="text-center py-10">Loading...</p>;

  return (
    <div className="relative min-h-screen">
    {/* Loading Indicator */}
      {loading && (
           <div className="fixed inset-0 flex items-center justify-center z-50">
             <div className="flex flex-col items-center p-3 bg-black border border-white rounded-2xl">
               <FadeLoader 
                 height={15}
                 width={5}
                 color="#ffffff"
                 ariaLabel="loading"
               />
               <h1 className="mt-1 text-lg text-white">Please wait...</h1>
             </div>
           </div>
         )}
         
    <div className="container mx-auto px-4 md:px-6 lg:px-8  xl:px-24">
      <h1 className="text-2xl md:text-3xl font-semibold text-blue-600 mb-3 md:mb-6 text-center md:text-left">
        Booking Information
      </h1>
      
      {/* Controls - Responsive */}
      <div className="p-2 md:p-6 font-sans flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
        <div className="w-full md:w-auto">
          <label className="block text-base md:text-lg font-medium mb-2">
            Search:
            <input
              type="search"
              placeholder="Search bookings..."
              className="mt-1 p-2 w-full md:w-48 border rounded-md focus:ring-2 focus:ring-blue-500 text-sm"
              onChange={handleSearch}
            />
          </label>
        </div>

        {/* Export Options - Responsive */}
        <div className="flex flex-wrap justify-center w-full md:w-auto items-center gap-2 md:gap-4">
          <button
            className="bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold text-xs md:text-sm py-1 px-2 md:px-3 rounded-full shadow hover:shadow-md hover:from-green-600 hover:to-green-700 transition-all duration-300 flex items-center gap-1"
            onClick={handleCopy}
          >
            <FaCopy className="text-xs md:text-sm" />
            <span className="hidden md:inline">Copy</span>
          </button>

          <button
            className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-white font-semibold text-xs md:text-sm py-1 px-2 md:px-3 rounded-full shadow hover:shadow-md hover:from-yellow-500 hover:to-yellow-600 transition-all duration-300 flex items-center gap-1"
            onClick={handleCsvExport}
          >
            <FaFileCsv className="text-xs md:text-sm" />
            <span className="hidden md:inline">CSV</span>
          </button>

          <button
            className="bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold text-xs md:text-sm py-1 px-2 md:px-3 rounded-full shadow hover:shadow-md hover:from-blue-600 hover:to-blue-700 transition-all duration-300 flex items-center gap-1"
            onClick={handleExcelExport}
          >
            <FaFileExcel className="text-xs md:text-sm" />
            <span className="hidden md:inline">Excel</span>
          </button>

          <button
            className="bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold text-xs md:text-sm py-1 px-2 md:px-3 rounded-full shadow hover:shadow-md hover:from-red-600 hover:to-red-700 transition-all duration-300 flex items-center gap-1"
            onClick={handlePdfExport}
          >
            <FaFilePdf className="text-xs md:text-sm" />
            <span className="hidden md:inline">PDF</span>
          </button>
        </div>

        <div className="w-full md:w-auto flex items-center">
          <label htmlFor="time-dropdown" className="text-base md:text-lg font-medium">
            Show
            <select
              id="time-dropdown"
              value={entriesPerPage}
              onChange={handleChange}
              className="mx-2 p-1 md:p-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-500"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            entries
          </label>
        </div>
      </div>

      {/* Desktop View - Table */}
      {!isMobile && (
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full table-auto border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-100">
                <th className="px-4 py-2 text-sm font-medium text-gray-900 border-b border-gray-200">
                  Service Title
                </th>
                <th className="px-4 py-2 text-sm font-medium text-gray-900 border-b border-gray-200">
                  Order Date
                </th>
                <th className="px-4 py-2 text-sm font-medium text-gray-900 border-b border-gray-200">
                  App Date
                </th>
                <th className="px-4 py-2 text-sm font-medium text-gray-900 border-b border-gray-200">
                  Customer
                </th>
                <th className="px-4 py-2 text-sm font-medium text-gray-900 border-b border-gray-200">
                  Phone
                </th>
                <th className="px-4 py-2 text-sm font-medium text-gray-900 border-b border-gray-200">
                  Address
                </th>
                <th className="px-4 py-2 text-sm font-medium text-gray-900 border-b border-gray-200">
                  Status
                </th>
                <th className="px-4 py-2 text-sm font-medium text-gray-900 border-b border-gray-200">
                  More
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedServices.map((service, index) => (
                <tr key={index} className="border-t border-gray-200 hover:bg-gray-50">
                  <td className="px-4 py-2 text-sm text-gray-900 border-b border-gray-200">
                    {service.service_title}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-900 border-b border-gray-200">
                    {service.order_date}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-900 border-b border-gray-200">
                    {service.start_date}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-900 border-b border-gray-200">
                    {service.client_name}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-900 border-b border-gray-200">
                    {service.client_phone}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-900 border-b border-gray-200">
                    {service.client_address}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-900 border-b border-gray-200">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      service.booking_status === 'Completed' ? 'bg-green-100 text-green-800' :
                      service.booking_status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                      service.booking_status === 'Cancelled' ? 'bg-red-100 text-red-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {service.booking_status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-900 border-b border-gray-200">
                    <div className="flex space-x-2">
                      <button
                        className="text-blue-500 flex items-center gap-1 p-1 border rounded-md hover:bg-blue-100"
                        onClick={() => handleAddonsClick(service)}
                      >
                        <FaInfoCircle className="text-xs" />
                        Addons
                      </button>
                      <button
                        className="text-yellow-500 flex items-center gap-1 p-1 border rounded-md hover:bg-yellow-100"
                        onClick={() => handleMethodsClick(service)}
                      >
                        <FaCogs className="text-xs" />
                        Method
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Mobile View - Cards */}
      {isMobile && (
        <div className="md:hidden space-y-4">
          {paginatedServices.map((service, index) => (
            <div key={index} className="bg-white border rounded-lg shadow-sm p-4">
              <div className="flex justify-between items-start mb-3">
                <h3 className="text-lg font-semibold text-blue-600">{service.service_title}</h3>
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                  service.booking_status === 'Completed' ? 'bg-green-100 text-green-800' :
                  service.booking_status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                  service.booking_status === 'Cancelled' ? 'bg-red-100 text-red-800' :
                  'bg-blue-100 text-blue-800'
                }`}>
                  {service.booking_status}
                </span>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Customer:</span>
                  <span className="font-medium">{service.client_name}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-gray-600">Phone:</span>
                  <span className="font-medium">{service.client_phone}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-gray-600">Address:</span>
                  <span className="font-medium truncate max-w-[200px]">{service.client_address}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-gray-600">Order Date:</span>
                  <span className="font-medium">{service.order_date}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-gray-600">App Date:</span>
                  <span className="font-medium">{service.start_date}</span>
                </div>
              </div>
              
              <div className="mt-4 flex justify-between">
                <button
                  className="flex-1 mr-2 bg-blue-100 text-blue-700 py-2 px-3 rounded-md text-sm font-medium flex items-center justify-center gap-1"
                  onClick={() => handleAddonsClick(service)}
                >
                  <FaInfoCircle className="text-xs" />
                  Addons
                </button>
                <button
                  className="flex-1 ml-2 bg-yellow-100 text-yellow-700 py-2 px-3 rounded-md text-sm font-medium flex items-center justify-center gap-1"
                  onClick={() => handleMethodsClick(service)}
                >
                  <FaCogs className="text-xs" />
                  Method
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Addons Modal - Responsive */}
      {showAddonsModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50 p-4">
          <div className="bg-white p-4 md:p-6 rounded-lg w-full max-w-[800px] max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center">
              <h2 className="text-lg md:text-xl font-semibold">
                Addons for {selectedService?.service_title}
              </h2>
              <button onClick={closeAddonsModal} className="text-red-500">
                <FaTimesCircle />
              </button>
            </div>

            <div className="overflow-x-auto mt-4">
              {isMobile ? (
                // Mobile card view for addons
                <div className="space-y-3">
                  {selectedAddons?.length > 0 ? (
                    selectedAddons.map((addon, index) => (
                      <div key={index} className="border rounded-md p-3 bg-gray-50">
                        <h3 className="font-semibold text-blue-600">{addon.addon_service_name}</h3>
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          <div className="text-gray-600">Quantity:</div>
                          <div className="font-medium">{addon.quantity}</div>
                          <div className="text-gray-600">Rate:</div>
                          <div className="font-medium">{addon.rate}</div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-center py-4 text-gray-500">No addons available</p>
                  )}
                </div>
              ) : (
                // Desktop table view for addons
                <table className="min-w-full table-auto border-collapse border border-gray-300">
                  <thead>
                    <tr>
                      <th className="p-3 border text-left">Addon Name</th>
                      <th className="p-3 border text-left">Addon Quantity</th>
                      <th className="p-3 border text-left">Addon Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedAddons?.length > 0 ? (
                      selectedAddons.map((addon, index) => (
                        <tr key={index}>
                          <td className="p-3 border">{addon.addon_service_name}</td>
                          <td className="p-3 border">{addon.quantity}</td>
                          <td className="p-3 border">{addon.rate}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="p-3 border text-center">No addons available</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Methods Modal - Responsive */}
      {showMethodsModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50 p-4">
          <div className="bg-white p-4 md:p-6 rounded-lg w-full max-w-[800px] max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center">
              <h2 className="text-lg md:text-xl font-semibold">
                Methods for {selectedService?.service_title}
              </h2>
              <button onClick={closeMethodsModal} className="text-red-500">
                <FaTimesCircle />
              </button>
            </div>
            <div className="overflow-x-auto mt-4">
              {isMobile ? (
                // Mobile card view for methods
                <div className="space-y-3">
                  {selectedMethods?.length > 0 ? (
                    selectedMethods.map((method, index) => (
                      <div key={index} className="border rounded-md p-3 bg-gray-50">
                        <h3 className="font-semibold text-blue-600">{method.unit_title}</h3>
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          <div className="text-gray-600">Unit Title:</div>
                          <div className="font-medium">{method.unit_title}</div>
                          <div className="text-gray-600">Quantity:</div>
                          <div className="font-medium">{method.quantity}</div>
                          <div className="text-gray-600">Rate:</div>
                          <div className="font-medium">{method.rate}</div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-center py-4 text-gray-500">No methods available</p>
                  )}
                </div>
              ) : (
                // Desktop table view for methods
                <table className="min-w-full table-auto border-collapse border border-gray-300">
                  <thead>
                    <tr>
                      <th className="p-3 border text-left">Method Title</th>
                      <th className="p-3 border text-left">Method Unit Title</th>
                      <th className="p-3 border text-left">Method Quantity</th>
                      <th className="p-3 border text-left">Method Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedMethods?.length > 0 ? (
                      selectedMethods.map((method, index) => (
                        <tr key={index}>
                          <td className="p-3 border">{method.unit_title}</td>
                          <td className="p-3 border">{method.unit_title}</td>
                          <td className="p-3 border">{method.quantity}</td>
                          <td className="p-3 border">{method.rate}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="p-3 border text-center">No methods available</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Pagination - Responsive */}
      <div className="flex items-center justify-center md:justify-end mt-4 flex-wrap">
        <button
          onClick={handlePrevPage}
          disabled={currentPage === 1}
          className="px-2 md:px-4 py-1 md:py-2 bg-gray-300 rounded-l hover:bg-gray-400 disabled:opacity-50 text-sm"
        >
          Prev
        </button>
        {[...Array(totalPages).keys()].map((page) => (
          <button
            key={page + 1}
            onClick={() => handlePageChange(page + 1)}
            className={`px-2 md:px-4 py-1 md:py-2 border text-sm ${
              currentPage === page + 1
                ? "bg-blue-500 text-white"
                : "bg-gray-200 hover:bg-gray-300"
            }`}
          >
            {page + 1}
          </button>
        ))}
        <button
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
          className="px-2 md:px-4 py-1 md:py-2 bg-gray-300 rounded-r hover:bg-gray-400 disabled:opacity-50 text-sm"
        >
          Next
        </button>
      </div>
    </div>
    
  </div>
  );
};

export default Export;


// import { FaTimesCircle } from "react-icons/fa";
// import axios from "axios";
// import { FaCopy, FaFileCsv, FaFileExcel, FaFilePdf } from "react-icons/fa";
// import * as XLSX from "xlsx";
// import jsPDF from "jspdf";
// import "jspdf-autotable";

// // Serviceinformation Component
// const Export = () => {
//   const [services, setServices] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [selectedAddons, setSelectedAddons] = useState([]);
//   const [selectedMethods, setSelectedMethods] = useState([]);
//   const [showAddonsModal, setShowAddonsModal] = useState(false);
//   const [showMethodsModal, setShowMethodsModal] = useState(false);
//   const [selectedService, setSelectedService] = useState(null);
//   const [searchQuery, setSearchQuery] = useState("");
//   const [selectedValue, setSelectedValue] = useState(10);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [entriesPerPage, setEntriesPerPage] = useState(10);

//   // Fetch service data on mount
//   useEffect(() => {
//     const fetchServicesData = async () => {
//       setLoading(true);
//       try {
//         const response = await axios.post(
//           "http://10.27.1.112:80/megaclean/backend/api/appointment_details.php",
//           {
//             action: "get_all_bookings",
//             api_key: "1",
//           }
//         );
//         if (response.data.status === "true") {
//           setServices(response.data.response); // Save services data
//         } else {
//           console.error("Failed to load services.");
//         }
//       } catch (error) {
//         console.error("Error fetching services:", error);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchServicesData();
//   }, []);

//   const handleAddonsClick = (service) => {
//     setSelectedService(service);
//     console.log("Selected Addons: ", service.addons_details); // Verify API response data
//     setSelectedAddons(service.addons_details || []);
//     setShowAddonsModal(true);
//   };

//   const handleMethodsClick = (service) => {
//     setSelectedService(service);
//     console.log("Selected Methods: ", service.unit_details); // Verify API response data
//     setSelectedMethods(service.unit_details || []);
//     setShowMethodsModal(true);
//   };

//   // Close Addons Modal
//   const closeAddonsModal = () => setShowAddonsModal(false);
//   // Close Methods Modal
//   const closeMethodsModal = () => setShowMethodsModal(false);

//   // Handle Search Input Change
//   const handleSearch = (e) => {
//     setSearchQuery(e.target.value.toLowerCase());
//   };

//   // Handle Copy functionality (Copy table content to clipboard)
//   const handleCopy = () => {
//     const tableContent = filteredServices
//       .map((service) => {
//         return [
//           service.id,
//           service.service_title,
//           service.order_date,
//           service.start_date,
//           service.client_name,
//           service.client_phone,
//           service.client_address,
//           service.booking_status,
//         ].join("\t");
//       })
//       .join("\n");

//     navigator.clipboard.writeText(tableContent).then(() => {
//       alert("Service data copied to clipboard!");
//     });
//   };

//   const handleChange = (event) => {
//     setEntriesPerPage(parseInt(event.target.value, 10));
//     setCurrentPage(1); // Reset to page 1 when entries per page change
//   };

//   // Filter services based on search query
//   const filteredServices = services.filter((service) => {
//     const serviceTitle = service.service_title ? service.service_title.toLowerCase() : '';
//     const clientName = service.client_name ? service.client_name.toLowerCase() : '';
//     const orderDate = service.order_date ? service.order_date.toLowerCase() : '';
//     const startDate = service.start_date ? service.start_date.toLowerCase() : '';
//     const clientPhone = service.client_phone ? service.client_phone.toLowerCase() : '';

//     return (
//       serviceTitle.includes(searchQuery) ||
//       clientName.includes(searchQuery) ||
//       orderDate.includes(searchQuery) ||
//       startDate.includes(searchQuery) ||
//       clientPhone.includes(searchQuery)
//     );
//   });

//   // Pagination logic
//   const startIndex = (currentPage - 1) * entriesPerPage;
//   const paginatedServices = filteredServices.slice(
//     startIndex,
//     startIndex + entriesPerPage
//   );
//   const totalPages = Math.ceil(filteredServices.length / entriesPerPage);

//   // Export to Excel
//   const handleExcelExport = () => {
//     const columns = [
//       "Service ID", "Service Title", "Order Date", "App Date", "Customer", "Phone", "Address", "Status"
//     ];
//     const rows = services.map((service) => [
//       service.id,
//       service.service_title,
//       service.order_date,
//       service.start_date,
//       service.client_name,
//       service.client_phone,
//       service.client_address,
//       service.booking_status,
//     ]);

//     const ws = XLSX.utils.aoa_to_sheet([columns, ...rows]);
//     const wb = XLSX.utils.book_new();
//     XLSX.utils.book_append_sheet(wb, ws, "Services Data");
//     XLSX.writeFile(wb, "services-data.xlsx");
//   };

//   // Export to PDF
//   const handlePdfExport = () => {
//     const doc = new jsPDF();
//     const columns = [
//       "Service ID", "Service Title", "Order Date", "App Date", "Customer", "Phone", "Address", "Status"
//     ];
//     const rows = services.map((service) => [
//       service.id,
//       service.service_title,
//       service.order_date,
//       service.start_date,
//       service.client_name,
//       service.client_phone,
//       service.client_address,
//       service.booking_status,
//     ]);

//     doc.autoTable({
//       head: [columns],
//       body: rows,
//       startY: 20,
//       theme: "grid",
//       margin: { horizontal: 10 },
//       headStyles: {
//         fillColor: [0, 123, 255],
//         textColor: 255,
//       },
//     });
//     doc.save("services-data.pdf");
//   };

//   const handleCsvExport = () => {
//     const csvData = services.map((service) => [
//       service.id,
//       service.service_title,
//       service.order_date,
//       service.start_date,
//       service.client_name,
//       service.client_phone,
//       service.client_address,
//       service.booking_status,
//     ]);

//     const csvContent = `data:text/csv;charset=utf-8,${csvData.join("\n")}`;
//     const encodedUri = encodeURI(csvContent);
//     const link = document.createElement("a");
//     link.setAttribute("href", encodedUri);
//     link.setAttribute("download", "services-data.csv");
//     link.click();
//   };

//   // Loading state
//   if (loading) return <p>Loading...</p>;

//   return (
//     <div className="max-w-7xl mx-auto p-6 bg-white shadow-lg rounded-md">
//       <h1 className="text-3xl font-semibold text-blue-600 mb-6">
//         Booking Information
//       </h1>
//       <div className="p-6 font-sans flex flex-col sm:flex-row justify-between items-center">
//   {/* Search Bar */}
//   <div className="flex items-center mb-4 sm:mb-0">
//     <label className="block text-lg font-medium mb-2 sm:mr-4">
//       Search:
//       <input
//         type="search"
//         placeholder="Search by Name, Email or Phone"
//         className="mt-2 p-2 w-full sm:w-48 border rounded-md focus:ring-2 focus:ring-blue-500 text-sm"
//         onChange={handleSearch}
//       />
//     </label>
//   </div>

//   {/* Export Options */}
//   <div className="flex flex-wrap justify-center sm:justify-start items-center gap-4 sm:gap-6 md:gap-8 px-2 sm:px-4 mb-4 sm:mb-0">
//     <button
//       className="bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold text-sm sm:text-base py-1 sm:py-2 px-2 sm:px-4 rounded-full shadow-lg hover:shadow-xl hover:from-green-600 hover:to-green-700 transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 flex items-center gap-2"
//       onClick={handleCopy}
//     >
//       <FaCopy className="text-sm sm:text-lg" />
//       Copy
//     </button>

//     <button
//       className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-white font-semibold text-sm sm:text-base py-1 sm:py-2 px-2 sm:px-4 rounded-full shadow-lg hover:shadow-xl hover:from-yellow-600 hover:to-yellow-700 transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 flex items-center gap-2"
//       onClick={handleCsvExport}
//     >
//       <FaFileCsv className="text-sm sm:text-lg" />
//       CSV
//     </button>

//     <button
//       className="bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold text-sm sm:text-base py-1 sm:py-2 px-2 sm:px-4 rounded-full shadow-lg hover:shadow-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 flex items-center gap-2"
//       onClick={handleExcelExport}
//     >
//       <FaFileExcel className="text-sm sm:text-lg" />
//       Excel
//     </button>

//     <button
//       className="bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold text-sm sm:text-base py-1 sm:py-2 px-2 sm:px-4 rounded-full shadow-lg hover:shadow-xl hover:from-red-600 hover:to-red-700 transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 flex items-center gap-2"
//       onClick={handlePdfExport}
//     >
//       <FaFilePdf className="text-sm sm:text-lg" />
//       PDF
//     </button>
//   </div>

//   {/* Entries Dropdown */}
//   <div className="flex items-center">
//     <label htmlFor="time-dropdown" className="text-lg font-medium mr-2">
//       Show
//       <select
//         id="time-dropdown"
//         value={selectedValue}
//         onChange={handleChange}
//         className="ml-2 p-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-500"
//       >
//         <option value={10}>10</option>
//         <option value={25}>25</option>
//         <option value={50}>50</option>
//         <option value={100}>100</option>
//       </select>
//       entries
//     </label>
//   </div>
// </div>


//     {/* Mobile Card View */}
// <div className="block sm:hidden">
//   {paginatedServices.map((service, index) => (
//     <div
//       key={index}
//       className="bg-gray-100 p-4 mb-4 rounded-md shadow-md flex flex-col"
//     >
//       <h2 className="font-bold text-lg">{service.service_title}</h2>
//       <p className="text-gray-700">
//         <strong>Order Date:</strong> {service.order_date}
//       </p>
//       <p className="text-gray-700">
//         <strong>App Date:</strong> {service.start_date}
//       </p>
//       <p className="text-gray-700">
//         <strong>Customer:</strong> {service.client_name}
//       </p>
//       <p className="text-gray-700">
//         <strong>Phone:</strong> {service.client_phone}
//       </p>
//       <p className="text-gray-700">
//         <strong>Status:</strong> {service.booking_status}
//       </p>

//       {/* Buttons Section */}
//       <div className="mt-4 flex justify-between items-center">
//         <button
//           className="bg-blue-500 text-white py-1 px-3 text-sm rounded-full"
//           onClick={() => handleAddonsClick(service)}
//         >
//           View Addons
//         </button>
//         <button
//           className="bg-yellow-500 text-white py-1 px-3 text-sm rounded-full"
//           onClick={() => handleMethodsClick(service)}
//         >
//           View Methods
//         </button>
//       </div>
//     </div>
//   ))}
// </div>


//       {/* Tablet & Laptop Table View */}
//       <div className="hidden sm:block">
//         <div className="overflow-x-auto">
//           <table className="min-w-full table-auto border-collapse border border-gray-300">
//             <thead>
//               <tr className="bg-gray-100">
//                 <th className="px-6 py-3 text-sm font-medium text-gray-900 border-b border-gray-200">
//                   Service Title
//                 </th>
//                 <th className="px-6 py-3 text-sm font-medium text-gray-900 border-b border-gray-200">
//                   Order Date
//                 </th>
//                 <th className="px-6 py-3 text-sm font-medium text-gray-900 border-b border-gray-200">
//                   App Date
//                 </th>
//                 <th className="px-6 py-3 text-sm font-medium text-gray-900 border-b border-gray-200">
//                   Customer
//                 </th>
//                 <th className="px-6 py-3 text-sm font-medium text-gray-900 border-b border-gray-200">
//                   Phone
//                 </th>
//                 <th className="px-6 py-3 text-sm font-medium text-gray-900 border-b border-gray-200">
//                   Address
//                 </th>
//                 <th className="px-6 py-3 text-sm font-medium text-gray-900 border-b border-gray-200">
//                   Status
//                 </th>
//                 <th className="px-6 py-3 text-sm font-medium text-gray-900 border-b border-gray-200">
//                   More
//                 </th>
//               </tr>
//             </thead>
//             <tbody>
//               {paginatedServices.map((service, index) => (
//                 <tr key={index} className="border-t border-gray-200">
//                   <td className="px-6 py-3 text-sm text-gray-900 border-b border-gray-200">
//                     {service.service_title}
//                   </td>
//                   <td className="px-6 py-3 text-sm text-gray-900 border-b border-gray-200">
//                     {service.order_date}
//                   </td>
//                   <td className="px-6 py-3 text-sm text-gray-900 border-b border-gray-200">
//                     {service.start_date}
//                   </td>
//                   <td className="px-6 py-3 text-sm text-gray-900 border-b border-gray-200">
//                     {service.client_name}
//                   </td>
//                   <td className="px-6 py-3 text-sm text-gray-900 border-b border-gray-200">
//                     {service.client_phone}
//                   </td>
//                   <td className="px-6 py-3 text-sm text-gray-900 border-b border-gray-200">
//                     {service.client_address}
//                   </td>
//                   <td className="px-6 py-3 text-sm text-gray-900 border-b border-gray-200">
//                     {service.booking_status}
//                   </td>
//                   <td className="px-6 py-3 text-sm text-gray-900 border-b border-gray-200">
//                     <div className="flex space-x-2">
//                       <button
//                         className="text-blue-500 flex items-center gap-2 p-2 border rounded-md hover:bg-blue-100"
//                         onClick={() => handleAddonsClick(service)}
//                       >
//                         Addons
//                       </button>
//                       <button
//                         className="text-yellow-500 flex items-center gap-2 p-2 border rounded-md hover:bg-yellow-100"
//                         onClick={() => handleMethodsClick(service)}
//                       >
//                         Method
//                       </button>
//                     </div>
//                   </td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         </div>
//       </div>

//       {/* Pagination */}
//       <div className="mt-4 flex justify-center gap-4">
//         <button
//           className="px-4 py-2 bg-blue-500 text-white rounded-md"
//           onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
//         >
//           Prev
//         </button>
//         <span className="px-4 py-2 text-lg">Page {currentPage} of {totalPages}</span>
//         <button
//           className="px-4 py-2 bg-blue-500 text-white rounded-md"
//           onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
//         >
//           Next
//         </button>
//       </div>
//     </div>
//   );
// };

// export default Export;



// import React, { useState, useRef, useEffect } from 'react';

// import { PiSortAscendingLight, PiSortDescendingLight } from "react-icons/pi";
// import { CiSearch } from "react-icons/ci";
// import "react-datepicker/dist/react-datepicker.css";
// import { format, subDays, subMonths, startOfMonth, endOfMonth } from "date-fns";
// import { FaCopy, FaFileCsv, FaFileExcel, FaFilePdf } from 'react-icons/fa';
// import * as XLSX from 'xlsx'
// import saveAs from 'file-saver';
// import { jsPDF } from "jspdf";

// function Booking() {
//     const [selectedOption, setSelectedOption] = useState("Today");
//     const [startDate, setStartDate] = useState(format(new Date(), "yyyy-MM-dd"));
//     const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));
//     const [showDropdown, setShowDropdown] = useState(false);
//     const [showCalendars, setShowCalendars] = useState(false);
//     const calendarRef = useRef(null);
//     const [isModalOpen, setIsModalOpen] = useState(false);
//     const [file, setFile] = useState(null);
//     const [activeTab, setActiveTab] = useState('email');
//     const [tableData, setTableData] = useState([
//         { id: 1, Service: "HouseClean", Order_Date: "2023-12-01 10:00 AM", App_Date: "2023-12-05", Customer: "John Doe", Phone: "123-456-7890", Address: "123 Main St, City", Status: "Confirmed" },
//         { id: 2, Service: "BathroomClean", Order_Date: "2023-12-02 11:30 AM", App_Date: "2023-12-07", Customer: "Jane Smith", Phone: "987-654-3210", Address: "456 Elm St, Town", Status: "Pending" },
//         { id: 3, Service: "CarWash", Order_Date: "2023-12-03 02:00 PM", App_Date: "2023-12-10", Customer: "Bob Johnson", Phone: "555-123-4567", Address: "789 Oak St, Village", Status: "Completed" },
//         { id: 4, Service: "ParkingClean", Order_Date: "2023-12-04 03:30 PM", App_Date: "2023-12-12", Customer: "Alice Brown", Phone: "111-222-3333", Address: "321 Pine St, County", Status: "Confirmed" },
//         { id: 5, Service: "Service", Order_Date: "2023-12-05 09:00 AM", App_Date: "2023-12-15", Customer: "Charlie Davis", Phone: "444-555-6666", Address: "654 Maple St, District", Status: "Pending" },
//     ]);
//     const [sortedTable, setSortedTable] = useState(tableData);
//     const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });
//     const [successMessage, setSuccessMessage] = useState(false);
//     const [formData, setFormData] = useState({
//         id: null,
//         Service: "",
//         Order_Date: "",
//         App_Date: "",
//         Customer: "",
//         Phone: "",
//         Address: "",
//     });
//     const [isSidebarOpen, setIsSidebarOpen] = useState(false);
//     const [searchQuery, setSearchQuery] = useState("");
//     const [entriesPerPage, setEntriesPerPage] = useState(10);
//     const [currentPage, setCurrentPage] = useState(1);
//     const [showAddOnMenu, setShowAddOnMenu] = useState(false);
//     const [showMethodMenu, setShowMethodMenu] = useState(false);
//     const [selectedRowId, setSelectedRowId] = useState(null);

//     const handleSelect = (option) => {
//         setSelectedOption(option);
//     };

//     useEffect(() => {
//         const handleClickOutside = (event) => {
//             if (calendarRef.current && !calendarRef.current.contains(event.target)) {
//                 setShowCalendars(false);
//             }
//         };
//         document.addEventListener("mousedown", handleClickOutside);
//         return () => {
//             document.removeEventListener("mousedown", handleClickOutside);
//         };
//     }, []);

//     useEffect(() => {
//         if (selectedOption === "Custom Range") {
//             setShowCalendars(true);
//         }
//     }, [selectedOption]);

//     const updateDateRange = (option) => {
//         let start, end;
//         const today = new Date();

//         switch (option) {
//             case "Today":
//                 start = end = today;
//                 break;
//             case "Yesterday":
//                 start = end = subDays(today, 1);
//                 break;
//             case "Last 7 Days":
//                 start = subDays(today, 7);
//                 end = today;
//                 break;
//             case "Last 30 Days":
//                 start = subDays(today, 30);
//                 end = today;
//                 break;
//             case "This Month":
//                 start = startOfMonth(today);
//                 end = endOfMonth(today);
//                 break;
//             case "Last Month":
//                 const firstDayLastMonth = startOfMonth(subDays(today, 30));
//                 const lastDayLastMonth = endOfMonth(subDays(today, 30));
//                 start = firstDayLastMonth;
//                 end = lastDayLastMonth;
//                 break;
//             case "Custom Range":
//                 const lastMonthStart = startOfMonth(subMonths(today, 1));
//                 const currentMonthEnd = today;
//                 setStartDate(format(lastMonthStart, "yyyy-MM-dd"));
//                 setEndDate(format(currentMonthEnd, "yyyy-MM-dd"));
//                 return;
//             default:
//                 start = end = today;
//         }
//         setStartDate(format(start, "yyyy-MM-dd"));
//         setEndDate(format(end, "yyyy-MM-dd"));
//     };

//     const handleSort = (column) => {
//         let direction = 'asc';
//         if (sortConfig.key === column && sortConfig.direction === 'asc') {
//             direction = 'desc';
//         }
//         setSortConfig({ key: column, direction });

//         const sortedData = [...tableData].sort((a, b) => {
//             if (a[column] < b[column]) return direction === 'asc' ? -1 : 1;
//             if (a[column] > b[column]) return direction === 'asc' ? 1 : -1;
//             return 0;
//         });

//         setSortedTable(sortedData);
//     };

//     const getSortIcon = (column) => {
//         if (sortConfig.key !== column) {
//             return <PiSortAscendingLight className="text-gray-500" />;
//         }
//         return sortConfig.direction === 'asc' ? <PiSortAscendingLight /> : <PiSortDescendingLight />;
//     };

//     useEffect(() => {
//         setSortedTable(tableData);
//     }, [tableData]);

//     const handleFileChange = (event) => {
//         setFile(event.target.files[0]);
//     };

//     const openModal = () => {
//         setIsModalOpen(true);
//     };

//     const closeModal = () => {
//         setIsModalOpen(false);
//     };

//     const handleSubmit = (e) => {
//         e.preventDefault();

//         const newData = {
//             id: formData.id || tableData.length + 1,
//             Service: formData.S_name.trim(),
//             Order_Date: formData.App_Date,
//             App_Date: formData.C_name,
//             Customer: formData.Status,
//             Phone: formData.Staff_name,
//             Address: formData.Net_Total,
//         };

//         if (formData.id) {
//             setTableData(tableData.map((row) =>
//                 row.id === formData.id ? newData : row
//             ));
//         } else {
//             setTableData([...tableData, newData]);
//         }

//         setFormData({
//             id: null,
//             Service: "",
//             Order_Date: "",
//             App_Date: "",
//             Customer: "",
//             Phone: "",
//             Address: "",
//         });

//         setSuccessMessage(true);
//         setIsSidebarOpen(false);

//         setTimeout(() => setSuccessMessage(false), 3000);
//     };

//     const toggleSidebar = () => {
//         setIsSidebarOpen(!isSidebarOpen);
//     };

//     const handleChange = (e) => {
//         const { name, value } = e.target;
//         setFormData({ ...formData, [name]: value });
//     };

//     const handleAddonClick = (rowId) => {
//         setSelectedRowId(rowId);
//         setShowAddOnMenu(true);
//         setShowMethodMenu(false);
//     };
//     const handleMethodclick = (rowId) => {
//         setSelectedRowId(rowId);
//         setShowMethodMenu(true);
//         setShowAddOnMenu(false);
//     };

//     const closeAddOnMenu = () => {
//         setShowAddOnMenu(false);
//         setSelectedRowId(null);
//     };

//     const closeMethodMenu = () => {
//         setShowMethodMenu(false);
//         setSelectedRowId(null);
//     };

//     const handleCopy = () => {
//         const tableContent = tableData.map(row => {
//             return Object.values(row).join('\t');
//         }).join('\n');
//         navigator.clipboard.writeText(tableContent).then(() => {
//             alert('Table data copied to clipboard!');
//         });
//     };

//     const handleCsvExport = () => {
//         const csvData = tableData.map(row => {
//             return Object.values(row).join(',');
//         });
//         const csvContent = `data:text/csv;charset=utf-8,${csvData.join('\n')}`;
//         const encodedUri = encodeURI(csvContent);
//         const link = document.createElement('a');
//         link.setAttribute('href', encodedUri);
//         link.setAttribute('download', 'tableData.csv');
//         link.click();
//     };

//     const handleExcelExport = () => {
//         const ws = XLSX.utils.json_to_sheet(tableData);
//         const wb = XLSX.utils.book_new();
//         XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
//         const excelFile = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
//         saveAs(new Blob([excelFile], { type: 'application/octet-stream' }), 'tableData.xlsx');
//     };

//     const handlePdfExport = () => {
//         const doc = new jsPDF();
//         doc.text('Table Data', 10, 10);
//         tableData.forEach((row, index) => {
//             doc.text(`${index + 1}. ${row.name} - ${row.email}`, 10, 20 + index * 10);
//         });
//         doc.save('tableData.pdf');
//     };

//     const filteredData = sortedTable.filter((row) =>
//         Object.values(row).some(value =>
//             value.toString().toLowerCase().includes(searchQuery.toLowerCase())
//         )
//     );

//     const startIndex = (currentPage - 1) * entriesPerPage;
//     const paginatedData = entriesPerPage === "All"
//         ? filteredData
//         : filteredData.slice(startIndex, startIndex + entriesPerPage);

//     const totalPages = Math.ceil(filteredData.length / entriesPerPage);

//     const handleSearch = (e) => setSearchQuery(e.target.value);

//     const handleEntriesChange = (e) => {
//         setEntriesPerPage(e.target.value === "All" ? "All" : parseInt(e.target.value, 10));
//         setCurrentPage(1);
//     };

//     const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
//     const handleNextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));
//     const handlePageChange = (page) => setCurrentPage(page);

//     const AddOnPopup = ({ rowId, onClose }) => (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
//             <div className="bg-white p-4 rounded-lg shadow-lg w-full max-w-4xl flex flex-col justify-between">
//                 {/* Title and Border */}
//                 <div className="flex flex-col items-start border-b-2 border-gray-300 pb-3 mb-4">
//                     <h2 className="text-lg font-bold">Service Methods {rowId}</h2>
//                 </div>

//                 {/* Search Bar and Buttons */}
//                 <div className="flex items-center justify-between mb-4">
//                     <input
//                         type="text"
//                         placeholder="Search..."
//                         className="w-full max-w-md p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                     />
//                     <div className="flex items-center gap-2 ml-4">
//                         <button
//                             onClick={handleCopy}
//                             className="bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold text-sm py-1 px-4 rounded shadow hover:from-green-600 hover:to-green-700"
//                         >
//                             Copy
//                         </button>
//                         <button
//                             onClick={handleCsvExport}
//                             className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-white font-semibold text-sm py-1 px-4 rounded shadow hover:from-yellow-500 hover:to-yellow-600"
//                         >
//                             CSV
//                         </button>
//                         <button
//                             onClick={handleExcelExport}
//                             className="bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold text-sm py-1 px-4 rounded shadow hover:from-blue-600 hover:to-blue-700"
//                         >
//                             Excel
//                         </button>
//                         <button
//                             onClick={handlePdfExport}
//                             className="bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold text-sm py-1 px-4 rounded shadow hover:from-red-600 hover:to-red-700"
//                         >
//                             PDF
//                         </button>
//                     </div>
//                     <div className="flex items-center gap-3 ml-4">
//                         <span className="text-sm">Show</span>
//                         <select
//                             value={entriesPerPage}
//                             onChange={handleEntriesChange}
//                             className="p-2 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm appearance-none"
//                         >
//                             <option value={10}>10</option>
//                             <option value={25}>25</option>
//                             <option value={50}>50</option>
//                             <option value="All">All</option>
//                         </select>
//                         <span className="text-sm">entries</span>
//                     </div>
//                 </div>

//                 {/* Table */}
//                 <table className="w-full border-collapse mb-4">
//                     <thead>
//                         <tr>
//                             <th className="border p-2">ServiceAddOnNames</th>
//                             <th className="border p-2">Base Price</th>
//                             <th className="border p-2">Base Quantity</th>
//                         </tr>
//                     </thead>
//                     <tbody>
//                         <tr>
//                             <td className="border p-2">Standard Clean</td>
//                             <td className="border p-2">$50</td>
//                             <td className="border p-2">1</td>
//                         </tr>
//                         <tr>
//                             <td className="border p-2">Deep Clean</td>
//                             <td className="border p-2">$100</td>
//                             <td className="border p-2">1</td>
//                         </tr>
//                     </tbody>
//                 </table>

//                 {/* Pagination (Previous and Next buttons) */}
//                 <div className="flex justify-end items-center mt-4">
//                     <div className="flex items-center gap-2">
//                         <button
//                             onClick={handlePrevPage}
//                             disabled={currentPage === 1}
//                             className="px-4 py-2 bg-gray-300 rounded-l hover:bg-gray-400 disabled:opacity-50"
//                         >
//                             Previous
//                         </button>
//                         {[...Array(totalPages).keys()].map((page) => (
//                             <button
//                                 key={page + 1}
//                                 onClick={() => handlePageChange(page + 1)}
//                                 className={`px-4 py-2 border ${currentPage === page + 1
//                                     ? "bg-blue-500 text-white"
//                                     : "bg-gray-200 hover:bg-gray-300"
//                                     }`}
//                             >
//                                 {page + 1}
//                             </button>
//                         ))}
//                         <button
//                             onClick={handleNextPage}
//                             disabled={currentPage === totalPages}
//                             className="px-4 py-2 bg-gray-300 rounded-r hover:bg-gray-400 disabled:opacity-50"
//                         >
//                             Next
//                         </button>
//                     </div>
//                 </div>

//                 {/* Close Button Centered at the Bottom */}
//                 <div className="flex justify-center mt-4">
//                     <button
//                         onClick={onClose} // Call the onClose function passed as a prop
//                         className="bg-blue-500 text-white px-4 py-2 rounded shadow hover:bg-blue-600"
//                     >
//                         Close
//                     </button>
//                 </div>
//             </div>
//         </div>

//     );

//     const MethodPopup = ({ rowId, onClose }) => (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
//             <div className="bg-white p-4 rounded-lg shadow-lg w-full max-w-4xl flex flex-col justify-between">
//                 {/* Title and Border */}
//                 <div className="flex flex-col items-start border-b-2 border-gray-300 pb-3 mb-4">
//                     <h2 className="text-lg font-bold">Service Methods {rowId}</h2>
//                 </div>

//                 {/* Search Bar and Buttons */}
//                 <div className="flex items-center justify-between mb-4">
//                     <input
//                         type="text"
//                         placeholder="Search..."
//                         className="w-full max-w-md p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                     />
//                     <div className="flex items-center gap-2 ml-4">
//                         <button
//                             onClick={handleCopy}
//                             className="bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold text-sm py-1 px-4 rounded shadow hover:from-green-600 hover:to-green-700"
//                         >
//                             Copy
//                         </button>
//                         <button
//                             onClick={handleCsvExport}
//                             className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-white font-semibold text-sm py-1 px-4 rounded shadow hover:from-yellow-500 hover:to-yellow-600"
//                         >
//                             CSV
//                         </button>
//                         <button
//                             onClick={handleExcelExport}
//                             className="bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold text-sm py-1 px-4 rounded shadow hover:from-blue-600 hover:to-blue-700"
//                         >
//                             Excel
//                         </button>
//                         <button
//                             onClick={handlePdfExport}
//                             className="bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold text-sm py-1 px-4 rounded shadow hover:from-red-600 hover:to-red-700"
//                         >
//                             PDF
//                         </button>
//                     </div>
//                     <div className="flex items-center gap-3 ml-4">
//                         <span className="text-sm">Show</span>
//                         <select
//                             value={entriesPerPage}
//                             onChange={handleEntriesChange}
//                             className="p-2 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm appearance-none"
//                         >
//                             <option value={10}>10</option>
//                             <option value={25}>25</option>
//                             <option value={50}>50</option>
//                             <option value="All">All</option>
//                         </select>
//                         <span className="text-sm">entries</span>
//                     </div>
//                 </div>

//                 {/* Table */}
//                 <table className="w-full border-collapse mb-4">
//                     <thead>
//                         <tr>
//                             <th className="border p-2">PropertyTitle</th>
//                             <th className="border p-2">Status</th>

//                         </tr>
//                     </thead>
//                     <tbody>
//                         <tr>
//                             <td className="border p-2">Standard Clean</td>
//                             <td className="border p-2">$50</td>
//                             <td className="border p-2">1</td>
//                         </tr>
//                         <tr>
//                             <td className="border p-2">Deep Clean</td>
//                             <td className="border p-2">$100</td>
//                             <td className="border p-2">1</td>
//                         </tr>
//                     </tbody>
//                 </table>

//                 {/* Pagination (Previous and Next buttons) */}
//                 <div className="flex justify-end items-center mt-4">
//                     <div className="flex items-center gap-2">
//                         <button
//                             onClick={handlePrevPage}
//                             disabled={currentPage === 1}
//                             className="px-4 py-2 bg-gray-300 rounded-l hover:bg-gray-400 disabled:opacity-50"
//                         >
//                             Previous
//                         </button>
//                         {[...Array(totalPages).keys()].map((page) => (
//                             <button
//                                 key={page + 1}
//                                 onClick={() => handlePageChange(page + 1)}
//                                 className={`px-4 py-2 border ${currentPage === page + 1
//                                     ? "bg-blue-500 text-white"
//                                     : "bg-gray-200 hover:bg-gray-300"
//                                     }`}
//                             >
//                                 {page + 1}
//                             </button>
//                         ))}
//                         <button
//                             onClick={handleNextPage}
//                             disabled={currentPage === totalPages}
//                             className="px-4 py-2 bg-gray-300 rounded-r hover:bg-gray-400 disabled:opacity-50"
//                         >
//                             Next
//                         </button>
//                     </div>
//                 </div>

//                 {/* Close Button Centered at the Bottom */}
//                 <div className="flex justify-center mt-4">
//                     <button
//                         onClick={onClose} // Call the closeMethodMenu function passed as a prop
//                         className="bg-blue-500 text-white px-4 py-2 rounded shadow hover:bg-blue-600"
//                     >
//                         Close
//                     </button>

//                 </div>
//             </div>
//         </div>
//     );

//     return (
//         <div className="container mx-auto px-4 md:px-6 lg:px-8 xl:px-52 py-6">
//             <div className="text-3xl font-bold p-4">
//                 <span>Booking Information</span>
//             </div>

//             <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center w-full">
//                 <div className="flex flex-col sm:flex-row items-center">
//                     <div className="relative">
//                         <input
//                             type="text"
//                             placeholder="Search Booking inf..."
//                             value={searchQuery}
//                             onChange={handleSearch}
//                             className="p-3 lg:w-full sm:w-80 md:w-96 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300 pl-10"
//                         />
//                         <CiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
//                     </div>
//                     <div className="bg-gray-100 p-1 rounded-lg shadow-md mb-6 max-w-md sm:max-w-sm ml-5 mt-4 sm:mt-0">
//                     </div>
//                 </div>
//                 <div className="flex gap-4 flex-col sm:flex-row items-center mt-4 sm:mt-0">
//                     <div className="flex items-center">
//                         <button onClick={handleCopy} className="bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold text-lg py-2 px-5 rounded-full shadow-lg hover:shadow-xl hover:from-green-600 hover:to-green-700 transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 flex items-center gap-2">
//                             <FaCopy className="text-2xl" />
//                             Copy
//                         </button>
//                         <button onClick={handleCsvExport} className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-white font-semibold text-lg py-2 px-5 rounded-full shadow-lg hover:shadow-xl hover:from-yellow-600 hover:to-yellow-700 transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 flex items-center gap-2">
//                             <FaFileCsv className="text-2xl" />
//                             CSV
//                         </button>
//                         <button onClick={handleExcelExport} className="bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold text-lg m-2 py-2 px-5 rounded-full shadow-lg hover:shadow-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 flex items-center gap-2">
//                             <FaFileExcel className="text-2xl" />
//                             Excel
//                         </button>
//                         <button onClick={handlePdfExport} className="bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold text-lg py-2 px-5 rounded-full shadow-lg hover:shadow-xl hover:from-red-600 hover:to-red-700 transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 flex items-center gap-2">
//                             <FaFilePdf className="text-2xl" />
//                             PDF
//                         </button>
//                     </div>
//                     <div className="flex items-center gap-3 sm:gap-4 sm:mt-0">
//                         <span className="text-sm sm:text-base">Show</span>
//                         <select
//                             value={entriesPerPage}
//                             onChange={handleEntriesChange}
//                             className="p-2 sm:p-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base appearance-none"
//                         >
//                             <option value={10}>10</option>
//                             <option value={25}>25</option>
//                             <option value={50}>50</option>
//                             <option value="All">All</option>
//                         </select>
//                         <span className="text-sm sm:text-base">entries</span>
//                     </div>
//                 </div>
//             </div>
//             <div className="overflow-x-auto mt-6">
//                 <table className="min-w-full table-auto bg-white border border-gray-300 shadow-md rounded-lg hidden sm:table">
//                     <thead className="bg-blue-100 text-left">
//                         <tr>
//                             <th className="px-4 py-2 border-b cursor-pointer">#</th>
//                             <th className="px-4 py-2 border-b cursor-pointer" onClick={() => handleSort('Service')}>
//                                 <div className="flex items-center gap-2">Service {getSortIcon('Service')}</div>
//                             </th>
//                             <th className="px-4 py-2 border-b cursor-pointer" onClick={() => handleSort('Order_Date')}>
//                                 <div className="flex items-center gap-2">Order Date {getSortIcon('Order_Date')}</div>
//                             </th>
//                             <th className="px-4 py-2 border-b cursor-pointer" onClick={() => handleSort('App_Date')}>
//                                 <div className="flex items-center gap-2">App Date {getSortIcon('App_Date')}</div>
//                             </th>
//                             <th className="px-4 py-2 border-b cursor-pointer" onClick={() => handleSort('Customer')}>
//                                 <div className="flex items-center gap-2">Customer {getSortIcon('Customer')}</div>
//                             </th>
//                             <th className="px-4 py-2 border-b cursor-pointer" onClick={() => handleSort('Phone')}>
//                                 <div className="flex items-center gap-2">Phone {getSortIcon('Phone')}</div>
//                             </th>
//                             <th className="px-4 py-2 border-b cursor-pointer" onClick={() => handleSort('Address')}>
//                                 <div className="flex items-center gap-2">Address {getSortIcon('Address')}</div>
//                             </th>
//                             <th className="px-4 py-2 border-b cursor-pointer" onClick={() => handleSort('Status')}>
//                                 <div className="flex items-center gap-2">Status {getSortIcon('Status')}</div>
//                             </th>
//                             <th className="px-4 py-2 border-b">Actions</th>
//                         </tr>
//                     </thead>
//                     <tbody>
//                         {paginatedData.map((row, index) => (
//                             <tr key={row.id} className="hover:bg-gray-50">
//                                 <td className="px-4 py-2 border-b">{index + 1}</td>
//                                 <td className="px-4 py-2 border-b">{row.Service}</td>
//                                 <td className="px-4 py-2 border-b">{row.Order_Date}</td>
//                                 <td className="px-4 py-2 border-b">{row.App_Date}</td>
//                                 <td className="px-4 py-2 border-b">{row.Customer}</td>
//                                 <td className="px-4 py-2 border-b">{row.Phone}</td>
//                                 <td className="px-4 py-2 border-b">{row.Address}</td>
//                                 <td className="px-4 py-2 border-b">{row.Status}</td>
//                                 <td className="px-4 py-2 border-b">
//                                     <div className="flex space-x-2">
//                                         <button onClick={() => handleAddonClick(row.id)} className="bg-green-500 text-white font-semibold px-3 py-1 rounded-lg hover:bg-green-600 text-sm">
//                                             AddOn
//                                         </button>
//                                         <button onClick={() => handleMethodclick(row.id)} className="bg-red-500 text-white font-semibold px-2 py-1 rounded-lg hover:bg-red-600 text-sm">
//                                             Method
//                                         </button>
//                                     </div>
//                                 </td>
//                             </tr>
//                         ))}
//                     </tbody>
//                 </table>
//                 <div className="sm:hidden space-y-4">
//                     {paginatedData.map((row, index) => (
//                         <div key={row.id} className="bg-white border border-gray-300 shadow-md rounded-lg p-4">
//                             <div className="flex justify-between items-center mb-2">
//                                 <div className="font-bold text-lg">{row.Service}</div>
//                                 <div className="text-sm text-gray-500">{row.Order_Date}</div>
//                             </div>
//                             <div className="space-y-1 text-sm">
//                                 <p><span className="font-semibold">App Date:</span> {row.App_Date}</p>
//                                 <p><span className="font-semibold">Customer:</span> {row.Customer}</p>
//                                 <p><span className="font-semibold">Phone:</span> {row.Phone}</p>
//                                 <p><span className="font-semibold">Address:</span> {row.Address}</p>
//                                 <p><span className="font-semibold">Status:</span> {row.Status}</p>
//                             </div>
//                             <div className="flex justify-between mt-3">
//                                 <button onClick={() => handleAddonClick(row.id)} className="bg-green-500 text-white font-semibold px-3 py-1 rounded-lg hover:bg-green-600 text-sm">
//                                     AddOn
//                                 </button>
//                                 <button onClick={() => handleMethodclick(row.id)} className="bg-red-500 text-white font-semibold px-3 py-1 rounded-lg hover:bg-red-600 text-sm">
//                                     Method
//                                 </button>
//                             </div>
//                         </div>
//                     ))}
//                 </div>
//             </div>
//             <div className="flex justify-between items-center mt-4">
//                 <div className="text-sm text-gray-600">
//                     <span>Showing </span><span>{startIndex + 1}</span><span> to </span><span>{Math.min(startIndex + entriesPerPage, filteredData.length)}</span><span> of </span><span>{filteredData.length}</span><span> entries</span>
//                 </div>
//                 {entriesPerPage !== "All" && (
//                     <div className="flex items-center">
//                         <button
//                             onClick={handlePrevPage}
//                             disabled={currentPage === 1}
//                             className="px-4 py-2 bg-gray-300 rounded-l hover:bg-gray-400 disabled:opacity-50"
//                         >
//                             Previous
//                         </button>
//                         {[...Array(totalPages).keys()].map((page) => (
//                             <button
//                                 key={page + 1}
//                                 onClick={() => handlePageChange(page + 1)}
//                                 className={`px-4 py-2 border ${currentPage === page + 1
//                                     ? "bg-blue-500 text-white"
//                                     : "bg-gray-200 hover:bg-gray-300"
//                                     }`}
//                             >
//                                 {page + 1}
//                             </button>
//                         ))}
//                         <button
//                             onClick={handleNextPage}
//                             disabled={currentPage === totalPages}
//                             className="px-4 py-2 bg-gray-300 rounded-r hover:bg-gray-400 disabled:opacity-50"
//                         >
//                             Next
//                         </button>
//                     </div>
//                 )}
//             </div>
//             {/* {showAddOnMenu && <AddOnPopup rowId={selectedRowId} />}
//             {showMethodMenu && <MethodPopup rowId={selectedRowId} />} */}
//             {showAddOnMenu && <AddOnPopup rowId={selectedRowId} onClose={closeAddOnMenu} />}
//             {showMethodMenu && <MethodPopup rowId={selectedRowId} onClose={closeMethodMenu} />}
//         </div>
//     );
// }

// export default Booking;


